<app-tab-toolbar [tabId]="'audit2'" [selectedYear]="selectedYear" [saveDisabled]="!hasChanges" [showKeywordsToggle]="true" [keywordsVisible]="showKeywords" (keywordsVisibleChange)="showKeywords = $event" (yearChange)="onYearChange($event)" (save)="onSave()">
  @if (showYearWarning) {
    <div class="year-warning">
      <mat-icon>warning</mat-icon>
      <span>There is no data for the selected year</span>
    </div>
  }
</app-tab-toolbar>

<div class="columns-wrapper" [formGroup]="form" [appReadOnlyTab]="'audit2'">

  <!-- ── LEFT COLUMN ──────────────────────────────────────────────────── -->
  <div class="column">

    <!-- 1. Accounting Standard -->
    <section class="section-container nrg-border-radius-second-level">
      <div class="section-header">
        <span>Accounting Standard</span>
      </div>
      <div class="section-content">
        <mat-form-field appearance="fill" class="form-field">
          <mat-label>Accounting Standard</mat-label>
          <mat-select formControlName="accountingStandardId" panelClass="tall-select-panel">
            <mat-option value="">(None)</mat-option>
            @for (standard of accountingStandards; track standard.id) {
              <mat-option [value]="standard.id">{{ standard.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
    </section>

    <!-- 2. Opinion -->
    <section class="section-container nrg-border-radius-second-level">
      <div class="section-header">
        <span>Opinion</span>
      </div>
      <div class="section-content">
        <mat-form-field appearance="fill" class="form-field">
          <mat-label>Opinion</mat-label>
          <mat-select formControlName="auditOpinionId">
            <mat-option value="">(None)</mat-option>
            @for (opinion of auditOpinions; track opinion.id) {
              <mat-option [value]="opinion.id">{{ opinion.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="fill" class="form-field">
          <mat-label>Opinion Modified</mat-label>
          <mat-select formControlName="auditOpinionModifiedId">
            <mat-option value="">(None)</mat-option>
            @for (modified of auditOpinionModifieds; track modified.id) {
              <mat-option [value]="modified.id">{{ modified.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (isQualified) {
          <div class="qualified-options-section">
            @if (!isReadOnly()) {
              <div class="qualified-options-header">
                <div class="select-with-button">
                  <mat-form-field appearance="fill" class="form-field">
                    <mat-label>Select Qualified Option</mat-label>
                    <mat-select [(value)]="selectedQualifiedOptionId">
                      <mat-option value="">(None)</mat-option>
                      @for (option of availableQualifiedOptions; track option.id) {
                        <mat-option [value]="option.id">{{ option.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <button mat-icon-button color="primary" type="button" (click)="onAddQualifiedOption()" [disabled]="!selectedQualifiedOptionId">
                    <mat-icon>add_circle</mat-icon>
                  </button>
                </div>
              </div>
            }
            <app-generic-list
              [columnDefinitions]="qualifiedOptionColumnDefinitions"
              [items]="selectedQualifiedOptions"
              [enablePaging]="false"
              [enableDelete]="!isReadOnly()"
              (deleteClick)="onDeleteQualifiedOption($event)">
            </app-generic-list>
          </div>
        }
      </div>
    </section>

    <!-- 3. Emphasis of Matters -->
    <section class="section-container nrg-border-radius-second-level">
      <div class="section-header">
        <span>Emphasis of Matters</span>
      </div>
      <div class="section-content">
        @if (!isReadOnly()) {
          <div class="emphasis-input select-with-button">
            <app-autocomplete-select #emphasisAutocomplete
              [isFullWidth]="true"
              [options]="availableEmphasisOfMatters"
              displayProperty="name"
              label="Find an emphasis of matter"
              placeholder="Type to search..."
              panelClass="tall-select-panel"
              (optionSelected)="onEmphasisOfMatterPending($event)">
            </app-autocomplete-select>
            <button mat-icon-button color="primary" type="button" (click)="onAddEmphasisOfMatter()" [disabled]="!pendingEmphasisOfMatter">
              <mat-icon>add_circle</mat-icon>
            </button>
          </div>
        }
        <app-generic-list
          [columnDefinitions]="emphasisOfMatterColumnDefinitions"
          [items]="selectedEmphasisOfMatters"
          [enablePaging]="false"
          [enableDelete]="!isReadOnly()"
          (deleteClick)="onDeleteEmphasisOfMatter($event)">
        </app-generic-list>
      </div>
    </section>

    <!-- 4. Going Concern -->
    <section class="section-container nrg-border-radius-second-level">
      <div class="section-header">
        <span>Going Concern</span>
      </div>
      <div class="section-content">
        <mat-form-field appearance="fill" class="form-field">
          <mat-label>Going Concern</mat-label>
          <mat-select formControlName="hasGoingConcern">
            <mat-option value="">(None)</mat-option>
            <mat-option value="yes">Yes</mat-option>
            <mat-option value="no">No</mat-option>
          </mat-select>
        </mat-form-field>

        @if (isGoingConcernYes) {
          <div class="qualified-options-section">
            @if (!isReadOnly()) {
              <div class="qualified-options-header">
                <div class="select-with-button">
                  <mat-form-field appearance="fill" class="form-field">
                    <mat-label>Select Going Concern</mat-label>
                    <mat-select [(value)]="selectedGoingConcernId">
                      <mat-option value="">(None)</mat-option>
                      @for (concern of availableGoingConcerns; track concern.id) {
                        <mat-option [value]="concern.id">{{ concern.name }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <button mat-icon-button color="primary" type="button" (click)="onAddGoingConcern()" [disabled]="!selectedGoingConcernId">
                    <mat-icon>add_circle</mat-icon>
                  </button>
                </div>
              </div>
            }
            <app-generic-list
              [columnDefinitions]="goingConcernColumnDefinitions"
              [items]="selectedGoingConcerns"
              [enablePaging]="false"
              [enableDelete]="!isReadOnly()"
              (deleteClick)="onDeleteGoingConcern($event)">
            </app-generic-list>
          </div>
        }
      </div>
    </section>

    <!-- 5. Key Audit Matters -->
    <section class="section-container nrg-border-radius-second-level">
      <div class="section-header">
        <span>Key Audit Matters</span>
      </div>
      <div class="section-content">
        @if (!isReadOnly()) {
          <div class="emphasis-input select-with-button">
            <app-autocomplete-select #keyMatterAutocomplete
              [isFullWidth]="true"
              [options]="availableKeyMatters"
              [maxResults]="0"
              displayProperty="name"
              label="Find a key audit matter"
              placeholder="Type to search..."
              panelClass="tall-select-panel"
              (optionSelected)="onKeyMatterPending($event)">
            </app-autocomplete-select>
            <button mat-icon-button color="primary" type="button" (click)="onAddKeyMatter()" [disabled]="!pendingKeyMatter">
              <mat-icon>add_circle</mat-icon>
            </button>
          </div>
        }
        <app-generic-list
          [columnDefinitions]="keyMatterColumnDefinitions"
          [items]="selectedKeyMatters"
          [enablePaging]="false"
          [enableDelete]="!isReadOnly()"
          (deleteClick)="onDeleteKeyMatter($event)">
        </app-generic-list>
      </div>
    </section>

    <!-- 6. Materiality -->
    <section class="section-container nrg-border-radius-second-level">
      <div class="section-header">
        <span>Materiality</span>
      </div>
      <div class="section-content materiality-fields">
        <mat-form-field appearance="fill" class="materiality-value-field">
          <mat-label>Value</mat-label>
          <input matInput type="text" inputmode="decimal" appNumberFormat [min]="0" [max]="100" [decimals]="2" formControlName="materialityValue">
          <span matSuffix>%</span>
        </mat-form-field>

        <mat-form-field appearance="fill" class="materiality-select-field">
          <mat-label>Audit Materiality</mat-label>
          <mat-select formControlName="auditMaterialityId">
            <mat-option value="">(None)</mat-option>
            @for (materiality of auditMaterialities; track materiality.id) {
              <mat-option [value]="materiality.id">{{ materiality.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
    </section>

  </div>

  <!-- ── RIGHT COLUMN (KEYWORDS) ──────────────────────────────────────── -->
  @if (showKeywords) {
    <div class="column">
      <section class="section-container keywords-section-standalone nrg-border-radius-second-level" formGroupName="keywords">
        <div class="section-header">
          <span>Keywords</span>
        </div>
        <div class="keywords-fields">
          <app-keyword-input name="auditor" label="Auditor" />
          <app-keyword-input name="accountingStandard" label="Accounting Standard" />
          <app-keyword-input name="emphasisOfMatters" label="Emphasis of Matters" />
          <app-keyword-input name="keyAuditMatters" label="Key Audit Matters" />
        </div>
      </section>
    </div>
  }

</div>

<ngx-spinner name="primary" type="ball-spin-clockwise" [fullScreen]="false" size="medium" />
